import { produce } from 'immer';
import { getCountryCallingCode } from 'libphonenumber-js';
import { create } from 'zustand';
import type { StoreApi } from 'zustand';
import { persist } from 'zustand/middleware';

import { SupportedCountry } from '@/constants/country';
import { apiClient } from '@/lib/api/client';
import { NoOrganizationAccessError } from '@/lib/api/error.no-organization-access';
import { PersonalEmailNotAllowedError } from '@/lib/api/error.personal-email-not-allowed';
import { env, setSessionProvider } from '@/lib/env';
import { AUTH_EVENTS } from '@/lib/events/constants';
import { emitEvent } from '@/lib/events/helpers';
import type { SupportedLanguage } from '@/lib/i18n';
import { isSupportedLanguage } from '@/lib/i18n';
import { injectLogContext } from '@/lib/logger';
import { clearAllQueries } from '@/lib/query';
import { queryClient } from '@/lib/queryClient';
import { resetAllStores } from '@/lib/store/resettableStore';

import { authService, logoutService } from './ory/service';
import type { UserSession } from './types';
import { authStoreSchema, userSessionSchema } from './zod-schema';

export type { UserSession };

type AuthStore = {
  session: UserSession | null;
  locale: SupportedLanguage | null;
  location: SupportedCountry | null;
  setLocale: (locale: SupportedLanguage) => void;
  isAuthenticated: () => Promise<boolean>;
  getSession: () => Promise<UserSession>;
  getLocationDialCode: () => string | null;
  refreshUserSession: () => Promise<UserSession>;
  downloadSignedCookies: () => Promise<void>;
};

/**
 * Feature Flag用のセッションプロバイダーを更新するヘルパー関数
 */
const updateFeatureFlagSessionProvider = (session: UserSession) => {
  setSessionProvider({
    getSession: () => ({
      organizationId: session.organization.organizationId,
      featureFlags: session.featureFlags,
    }),
  });
};

/**
 * APIからセッション情報を取得してストアを更新するヘルパー関数
 */
async function fetchAndUpdateSession(
  apiClient: typeof import('@/lib/api/client').apiClient,
  userSessionSchema: typeof import('./zod-schema').userSessionSchema,
  set: StoreApi<AuthStore>['setState'],
): Promise<UserSession | null> {
  const res = await apiClient.GET('/v2/session');
  if (!res.data) {
    return null;
  }
  const session = userSessionSchema.parse(res.data);
  set(
    produce((draft: AuthStore) => {
      draft.session = session;
      draft.location = session.organization.location;
    }),
  );
  return session;
}

/**
 * ユーザーセッションをリフレッシュし、locale同期も処理するヘルパー関数
 */
async function refreshSession(
  apiClient: typeof import('@/lib/api/client').apiClient,
  userSessionSchema: typeof import('./zod-schema').userSessionSchema,
  set: StoreApi<AuthStore>['setState'],
): Promise<UserSession> {
  let session: UserSession | null;
  try {
    session = await fetchAndUpdateSession(apiClient, userSessionSchema, set);
  } catch (error) {
    // 個人メアドでログイン直後にカレント組織を正規化できず、通過できる組織が
    // 1 つも無いケース。/me の案内画面（全件 accessible=false ＋再ログイン導線）へ寄せる。
    if (
      error instanceof NoOrganizationAccessError ||
      error instanceof PersonalEmailNotAllowedError
    ) {
      emitEvent(AUTH_EVENTS.NO_ORGANIZATION_ACCESS);
      throw error;
    }
    throw error;
  }
  if (!session) {
    throw new Error('Failed to fetch user session from API');
  }

  // Feature Flag用のセッションプロバイダーを設定
  updateFeatureFlagSessionProvider(session);

  // 認証前にクライアントで設定されたlocaleがある場合、サーバーのsession.localeと同期
  // NOTE: この処理は初回ロード時のみ実行し、ループを防ぐ
  const currentState = useAuthStore.getState();
  if (currentState.locale && currentState.locale !== session.locale) {
    try {
      // 直接APIを呼び出してlocaleを更新（updateLocaleを使わないことでループを防ぐ）
      await apiClient.PATCH('/v2/me/locale', {
        body: { locale: currentState.locale },
      });

      // 更新後のセッション情報を再取得
      const updatedSession = await fetchAndUpdateSession(
        apiClient,
        userSessionSchema,
        set,
      );
      if (updatedSession) {
        // 更新されたセッションでプロバイダーも更新
        updateFeatureFlagSessionProvider(updatedSession);
        return updatedSession;
      }
    } catch (error) {
      console.warn('Failed to sync client locale to session:', error);
      // 同期に失敗してもセッション情報は返す
    }
  }

  return session;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      const store = {
        session: null,
        locale: null,
        location: null,
        getLocationDialCode: (): string | null => {
          if (!get().location) {
            return null;
          }
          return getCountryCallingCode(get().location as SupportedCountry);
        },
        setLocale: (locale: string): void => {
          if (!isSupportedLanguage(locale)) {
            throw new Error(`Unsupported locale: ${locale}`);
          }
          set(
            produce((draft: AuthStore) => {
              draft.locale = locale;
              if (draft.session) {
                draft.session.locale = locale;
              }
            }),
          );
        },
        isAuthenticated: async (): Promise<boolean> => {
          return await authService.isAuthenticated();
        },
        getSession: async (): Promise<UserSession> => {
          const { session, refreshUserSession } = get();
          if (session) {
            injectLogContext({
              dressCodeUserId: session.user.id,
              tenantId: session.organization.tenantId,
              organizationId: session.organization.organizationId,
            });
            updateFeatureFlagSessionProvider(session);
            return session;
          }
          return await refreshUserSession();
        },
        refreshUserSession: async (): Promise<UserSession> => {
          return await refreshSession(apiClient, userSessionSchema, set);
        },
        downloadSignedCookies: async (): Promise<void> => {
          if (env.VITE_SET_ASSET_COOKIE_SKIP) return;

          const res = await apiClient.POST('/v2/auth/signed-cookie', {});

          // レスポンスボディが存在する場合のみ success フラグをチェック
          const data = res.data;
          if (!data?.success) {
            throw new Error(
              `Failed to download signed cookies: Unexpected response success: ${data?.success}`,
            );
          }
        },
      };
      authStoreSchema.parse(store);
      return store;
    },
    {
      name: 'auth',
      version: 1,
      onRehydrateStorage: () => (state, error) => {
        if (error || !state?.session) return;
        updateFeatureFlagSessionProvider(state.session);
      },
    },
  ),
);

let logoutPromise: Promise<void> | null = null;

/**
 * ログアウトして `/auth/sign-in` へ遷移する。
 * 並行呼び出しは進行中の Promise を共有し、Ory ログアウトやリダイレクトが多重実行されない。
 */
export function logout(): Promise<void> {
  logoutPromise ??= runLogout().finally(() => {
    logoutPromise = null;
  });
  return logoutPromise;
}

async function runLogout(): Promise<void> {
  // Ory Kratos でログアウト（サーバー側のセッションを無効化）
  const logoutFlow = await logoutService.initFlow();
  if (logoutFlow.logout_token) {
    await logoutService.submitFlow(logoutFlow.logout_token);
  }

  try {
    await clearAllQueries(queryClient);
  } catch {
    // cancelQueries 失敗時もキャッシュ破棄は完了しているため続行する。
  }

  useAuthStore.setState(
    produce((draft: AuthStore) => {
      draft.session = null;
    }),
  );
  setSessionProvider(null);
  resetAllStores();

  window.location.href = '/auth/sign-in';
}
